// Per-cylinder summary computations + cylinder-finding builder, extracted
// from app.js. The bundle keeps cylinderMetricSummary, its uncached
// computation, the per-area sub-summaries (valve / cooling-rate /
// induction-leak), the local-flag score cap, the formatter helpers
// (cylinderScoreMarkup / cylinderScoreText), the body builder
// (cylinderFindingBody), and the top-level cylinderFinding all together —
// they are tightly coupled and share many of the same engine, sensor, and
// scoring deps. Exposed as a factory so the binding stays alive against
// the app's hoisted helpers and module state.

import {
  cylinderEgtBehaviorRawScore,
  sensitivityAdjustedCylinderEgtBehaviorScore,
} from "../analysis/egt.js";

export function createCylinderSummary({
  // analysis / math primitives
  finiteValues,
  peerAverage,
  cylinderKeys,
  extentFromValues,
  quantile,
  percentAboveThreshold,
  percentBelowThreshold,
  clampScore,
  // engine helpers
  engineWorkPoint,
  engineInfoForCylinderNumber,
  currentEngineDescriptors,
  engineNarrativeProbeLabel,
  engineScopedProbeLabel,
  // findings & sensor formatters
  cylinderSensorSummary,
  cylinderProbeNote,
  chtExposureNote,
  panelNumber,
  signedPanelNumber,
  evidenceClassForScore,
  scoreClass,
  scoreLabel,
  valveBandLabel,
  coolingRateScoreForRate,
  clusteredCoolingRateEvents,
  coolingRateNoteForRate,
  readableProbeLabel,
  findingCylinderReference,
  valveFinding,
  // state getters
  getFlightData,
  getHealthSummary,
  getDetectionSensitivity,
  currentFlightDerivedCache,
  calculateHealthSummary,
  // constants
  chtHealthReviewThresholdF,
  chtCautionThresholdF,
  chtCoolingRateTriggerFpm,
}) {
  function cylinderValveSummary(number, summary) {
    const valve = summary?.valve ?? {};
    const top = valve.details?.top ?? null;
    const topCylinder = top?.cylinder ?? null;
    const isFlagged = Number.isFinite(valve.score) && valve.score < 88 && topCylinder === number;
    if (!Number.isFinite(valve.score)) {
      return {
        score: null,
        note: valve.note ?? "Need stable EGT window",
        isFlagged: false,
      };
    }
    if (!isFlagged) {
      return {
        score: 96,
        note: Number.isFinite(topCylinder) && valve.score < 88
          ? `No local wobble flag; see ${findingCylinderReference(topCylinder).label}`
          : "No local wobble flag",
        isFlagged: false,
      };
    }

    const best = top?.best ?? {};
    const amplitude = Number.isFinite(best.amplitude) ? `${Math.round(best.amplitude)} F` : "elevated";
    const ratio = Number.isFinite(best.ratio) ? `${best.ratio.toFixed(1)}x peers` : "peer-relative";
    const frequency = best.frequency?.band && best.frequency.band !== "unclassified"
      ? `${valveBandLabel(best.frequency.band)}${Number.isFinite(best.frequency.frequencyCpm) ? ` ${best.frequency.frequencyCpm.toFixed(1)} cpm` : ""}`
      : "EGT wobble";
    const timing = valve.details?.timing;
    const timingPrefix = timing?.windowLabel ? `${timing.windowLabel}; ` : "";
    return {
      score: valve.score,
      note: `${timingPrefix}${frequency}; ${amplitude}, ${ratio}`,
      isFlagged: true,
      top,
    };
  }

  function capCylinderScoreForLocalFlags(score, scores) {
    if (!Number.isFinite(score)) return score;
    const finiteScores = scores.filter(Number.isFinite);
    if (finiteScores.some((value) => value < 72)) return Math.min(score, 70);
    if (finiteScores.some((value) => value < 88)) return Math.min(score, 86);
    return score;
  }

  function cylinderCoolingRateSummary(number, summary) {
    const metric = summary?.coolingRate;
    const threshold = metric?.details?.thresholdFpm ?? chtCoolingRateTriggerFpm;
    const item = metric?.details?.summaries?.find((candidate) => candidate.cylinder === number) ?? null;
    if (!item) {
      return {
        score: null,
        note: "No CHT cooling-rate data",
        maxRateFpm: null,
        isFlagged: false,
      };
    }
    const maxRateFpm = item.maxRateFpm ?? item.top?.rateFpm ?? null;
    const score = Number.isFinite(item.score)
      ? item.score
      : coolingRateScoreForRate(maxRateFpm, { eventCount: clusteredCoolingRateEvents(item.events ?? []).length, thresholdFpm: threshold });
    return {
      score,
      note: coolingRateNoteForRate(maxRateFpm, readableProbeLabel(item.key) || item.label || findingCylinderReference(number).label, threshold),
      maxRateFpm,
      threshold,
      isFlagged: Number.isFinite(score) && score < 88,
      top: item.top ?? null,
      events: item.events ?? [],
    };
  }

  function cylinderInductionLeakSummary(number, summary) {
    const metric = summary?.inductionLeak;
    const top = metric?.details?.top ?? null;
    const isFlagged = Number.isFinite(metric?.score)
      && metric.score < 88
      && Number.isFinite(top?.cylinder)
      && top.cylinder === number;
    if (!isFlagged) {
      return {
        score: null,
        note: "No local induction-leak cue",
        isFlagged: false,
        top: null,
      };
    }
    const egtRise = Number.isFinite(top.cylinderEgtChange)
      ? top.cylinderEgtChange
      : top.egtChange;
    const note = Number.isFinite(egtRise)
      ? `EGT rose ${Math.round(egtRise)} F as fuel/RPM dropped`
      : metric.note ?? "Induction-leak cue";
    return {
      score: metric.score,
      note,
      isFlagged: true,
      top,
    };
  }

  function cylinderMetricSummary(number, summary) {
    const flightData = getFlightData();
    const resolved = summary ?? getHealthSummary() ?? calculateHealthSummary(flightData);
    if (resolved) {
      const cache = currentFlightDerivedCache();
      let summaries = cache.cylinderMetricSummaries.get(resolved);
      if (!summaries) {
        summaries = new Map();
        cache.cylinderMetricSummaries.set(resolved, summaries);
      }
      const cacheKey = `${number}|${getDetectionSensitivity()}`;
      if (summaries.has(cacheKey)) return summaries.get(cacheKey);
      const metric = cylinderMetricSummaryUncached(number, resolved);
      summaries.set(cacheKey, metric);
      return metric;
    }
    return cylinderMetricSummaryUncached(number, resolved);
  }

  function cylinderMetricSummaryUncached(number, summary) {
    const flightData = getFlightData();
    const rows = flightData.filter(engineWorkPoint);
    const analysisRows = rows.length >= 30 ? rows : flightData;
    const chtKey = `cht${number}`;
    const egtKey = `egt${number}`;
    const chtValues = finiteValues(analysisRows, chtKey);
    const egtValues = finiteValues(analysisRows, egtKey);
    const chtPeerDeltas = analysisRows.map((point) => {
      const value = point[chtKey];
      const peer = peerAverage(point, cylinderKeys("cht"), chtKey);
      return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
    }).filter(Number.isFinite);
    const egtPeerDeltas = analysisRows.map((point) => {
      const value = point[egtKey];
      const peer = peerAverage(point, cylinderKeys("egt"), egtKey);
      return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
    }).filter(Number.isFinite);
    const egtSensor = cylinderSensorSummary(summary, egtKey);
    const chtSensor = cylinderSensorSummary(summary, chtKey);
    const egtSuspicion = egtSensor?.suspicion ?? 0;
    const chtSuspicion = chtSensor?.suspicion ?? 0;
    const probeScore = clampScore(100 - Math.min(82, Math.max(egtSuspicion, chtSuspicion)));
    const chtP95 = quantile(chtValues, 0.95);
    const chtMax = extentFromValues(chtValues)?.max ?? null;
    const chtAboveReviewThreshold = percentAboveThreshold(chtValues, chtHealthReviewThresholdF);
    const chtBelowCautionThreshold = percentBelowThreshold(chtValues, chtCautionThresholdF);
    const chtPeerP95 = quantile(chtPeerDeltas, 0.95);
    const egtPeerP90 = quantile(egtPeerDeltas.map(Math.abs), 0.9);
    const egtRawScore = cylinderEgtBehaviorRawScore(egtPeerP90, egtSuspicion);
    // Mirrors the flight-level CHT ladder: 380-400 F is a capped mild ramp
    // (cannot flag on its own), Watch pressure starts at 400 F, peaks at 420 F.
    const chtScore = clampScore(100
      - Math.min(8, Math.max(0, Math.min(chtP95 ?? 0, chtCautionThresholdF) - chtHealthReviewThresholdF) * 0.4)
      - Math.max(0, (chtP95 ?? 0) - chtCautionThresholdF) * 0.75
      - Math.max(0, (chtMax ?? 0) - 420) * 0.6
      - Math.max(0, Math.abs(chtPeerP95 ?? 0) - 35) * 0.55);
    const egtScore = sensitivityAdjustedCylinderEgtBehaviorScore(egtRawScore, getDetectionSensitivity());
    const valveMetric = cylinderValveSummary(number, summary);
    const coolingRateMetric = cylinderCoolingRateSummary(number, summary);
    const inductionLeakMetric = cylinderInductionLeakSummary(number, summary);
    const pairingScore = clampScore(100 - Math.max(0, (egtPeerP90 ?? 0) - 90) * 0.18 - Math.max(0, Math.abs(chtPeerP95 ?? 0) - 45) * 0.32);
    const peerScore = clampScore(100 - Math.max(0, Math.abs(chtPeerP95 ?? 0) - 35) * 0.42 - Math.max(0, (egtPeerP90 ?? 0) - 80) * 0.2);
    const scored = [
      [probeScore, 0.24],
      [chtScore, 0.2],
      [egtScore, 0.18],
      [valveMetric.score, 0.24],
      [inductionLeakMetric.score, 0.16],
      [coolingRateMetric.score, 0.08],
      [pairingScore, 0.08],
      [peerScore, 0.06],
    ].filter(([score]) => Number.isFinite(score));
    const totalWeight = scored.reduce((total, [, weight]) => total + weight, 0) || 1;
    const weightedScore = Math.round(scored.reduce((total, [score, weight]) => total + score * weight, 0) / totalWeight);
    const cylinderScore = capCylinderScoreForLocalFlags(weightedScore, [
      probeScore,
      chtScore,
      egtScore,
      valveMetric.score,
      inductionLeakMetric.score,
      coolingRateMetric.score,
      pairingScore,
      peerScore,
    ]);
    return {
      number,
      score: cylinderScore,
      className: scoreClass(cylinderScore),
      label: scoreLabel(cylinderScore),
      probeScore,
      probeNote: `EGT ${cylinderProbeNote(egtSensor)} · CHT ${cylinderProbeNote(chtSensor)}`,
      chtScore,
      chtNote: chtExposureNote({
        aboveReviewThreshold: chtAboveReviewThreshold,
        belowCautionThreshold: chtBelowCautionThreshold,
        peak: chtMax,
        score: chtScore,
      }),
      egtScore,
      egtNote: `EGT differed from peers by more than ${panelNumber(egtPeerP90, "°F")} in 10% of samples`,
      valveScore: valveMetric.score,
      valveNote: valveMetric.note,
      valveFlagged: valveMetric.isFlagged,
      valveTop: valveMetric.top ?? null,
      inductionLeakScore: inductionLeakMetric.score,
      inductionLeakNote: inductionLeakMetric.note,
      inductionLeakFlagged: inductionLeakMetric.isFlagged,
      inductionLeakTop: inductionLeakMetric.top ?? null,
      coolingRateScore: coolingRateMetric.score,
      coolingRateNote: coolingRateMetric.note,
      coolingRateFlagged: coolingRateMetric.isFlagged,
      coolingRateMaxFpm: coolingRateMetric.maxRateFpm,
      coolingRateTop: coolingRateMetric.top ?? null,
      pairingScore,
      pairingNote: `CHT peer gap ${panelNumber(chtPeerP95, " F")} · EGT peer gap ${panelNumber(egtPeerP90, " F")}`,
      peerScore,
      peerNote: `CHT ${signedPanelNumber(chtPeerP95 ?? null, " F")} from peers · EGT gap ${panelNumber(egtPeerP90, " F")}`,
      contextScore: 96,
      contextNote: "Fuel, bus, and phase context remain in full chart",
      egtSensor,
      chtSensor,
      chtP95,
      chtMax,
      chtAboveReviewThreshold,
      chtBelowCautionThreshold,
      chtPeerP95,
      egtPeerP90,
    };
  }

  function cylinderScoreMarkup(score) {
    return `${Number.isFinite(score) ? Math.round(score) : "--"}<span>/100</span>`;
  }

  function cylinderScoreText(score) {
    return `${Number.isFinite(score) ? Math.round(score) : "--"}/100`;
  }

  function cylinderFindingBody(cylinderLabel, metric) {
    if (metric.probeScore < 72) {
      return `Probe confidence is the limiting signal: ${metric.probeNote}. CHT reads ${metric.chtNote.toLowerCase()}, while EGT shows ${metric.egtNote.toLowerCase()}.`;
    }
    const reviewSignals = [
      metric.inductionLeakFlagged ? metric.inductionLeakNote : null,
      metric.coolingRateFlagged ? metric.coolingRateNote : null,
      metric.chtScore < 88 ? metric.chtNote : null,
      metric.egtScore < 88 ? metric.egtNote : null,
      metric.valveFlagged ? metric.valveNote : null,
    ].filter(Boolean);
    const primarySignals = reviewSignals.slice(0, 2);
    if (primarySignals.length) {
      const stableCht = metric.chtScore >= 88 && Number.isFinite(metric.chtMax)
        ? `CHT stayed controlled, peaking at ${panelNumber(metric.chtMax, " F")}.`
        : "";
      return [`Main cues: ${primarySignals.join("; ")}.`, stableCht].filter(Boolean).join(" ");
    }
    return `${cylinderLabel} has no dominant local cue. CHT peaked at ${panelNumber(metric.chtMax, " F")}, EGT peer spread was ${panelNumber(metric.egtPeerP90, " F")}, and probe confidence reads ${metric.probeNote}.`;
  }

  function cylinderFinding(number, summary) {
    const flightData = getFlightData();
    const resolved = summary ?? getHealthSummary() ?? calculateHealthSummary(flightData);
    const metric = cylinderMetricSummary(number, resolved);
    if (metric.valveFlagged) return valveFinding(resolved.valve);
    const info = engineInfoForCylinderNumber(number, currentEngineDescriptors());
    const cylinderLabel = info.label;
    const cylinderText = info.engine ? `${info.engine.label} cylinder ${info.cylinder?.number ?? number}` : `cylinder ${number}`;
    const egtLabel = engineNarrativeProbeLabel(`egt${number}`) || engineScopedProbeLabel(`egt${number}`, currentEngineDescriptors()) || `EGT${number}`;
    const chtLabel = engineNarrativeProbeLabel(`cht${number}`) || engineScopedProbeLabel(`cht${number}`, currentEngineDescriptors()) || `CHT${number}`;
    const title = `${cylinderLabel} review`;
    const body = cylinderFindingBody(cylinderLabel, metric);
    return {
      title,
      meta: `Cylinder score ${metric.score}/100 · ${metric.inductionLeakFlagged ? metric.inductionLeakNote : metric.probeNote}`,
      body,
      evidence: [
        { label: "CHT", value: metric.chtNote, detail: `Peer gap was ${signedPanelNumber(metric.chtPeerP95 ?? null, " F")} or less for 95% of samples`, className: evidenceClassForScore(metric.chtScore) },
        { label: "Cooling Rate", value: metric.coolingRateNote, detail: `Cooling-rate screen ${cylinderScoreText(metric.coolingRateScore)}`, className: evidenceClassForScore(metric.coolingRateScore) },
        { label: "EGT", value: metric.egtNote, detail: metric.probeNote, className: evidenceClassForScore(metric.egtScore) },
        ...(metric.inductionLeakFlagged ? [{ label: "Induction Leak", value: metric.inductionLeakNote, detail: `Induction-leak screen ${cylinderScoreText(metric.inductionLeakScore)}`, className: evidenceClassForScore(metric.inductionLeakScore) }] : []),
        { label: "EGT Rhythm", value: metric.valveNote, detail: `Rhythm screen ${cylinderScoreText(metric.valveScore)}`, className: evidenceClassForScore(metric.valveScore) },
      ],
      causes: [
        ["Probe or wiring quality on this cylinder", metric.probeScore < 72 ? 0.42 : 0.18],
        ["Normal cylinder variation versus peers", metric.peerScore >= 88 ? 0.32 : 0.18],
        ["Real CHT/EGT behavior on this cylinder", metric.chtScore < 88 || metric.egtScore < 88 || metric.valveScore < 88 ? 0.32 : 0.2],
        ["Power, mixture, or phase context", 0.2],
      ],
      actions: [
        ["medium", `Review ${egtLabel} and ${chtLabel} together in the focused chart`],
        ["medium", `Compare ${cylinderText} with the other cylinders in the full-engine view`],
        ["low", "Use the overall finding panel for the engine-wide triage call"],
      ],
      confidence: metric.probeScore < 72 ? "Medium" : "Low",
    };
  }

  return {
    cylinderValveSummary,
    capCylinderScoreForLocalFlags,
    cylinderCoolingRateSummary,
    cylinderInductionLeakSummary,
    cylinderMetricSummary,
    cylinderMetricSummaryUncached,
    cylinderScoreMarkup,
    cylinderScoreText,
    cylinderFindingBody,
    cylinderFinding,
  };
}
